import HeaderButtons from "components/Button/HeaderButtons";
import React from "react";
// TODO: Update this import path to point to your actual util file location
import { convertUtcToIst } from "util/Util";

interface DeviceSettingsData {
  screenshotEnabled: boolean;
  deviceCheckInterval: number;
  dailyBreakLimit: number;
  sessionUploadInterval: number;
  urlSessionUploadInterval: number;
  deviceStatusUploadInterval: number;
  presenceCheckInterval: number;
  presenceUploadInterval: number;
  scheduleTrackingEnabled: boolean;
  idleThreshold: number;
  screenshotInterval: number;
  workStartTime: string;
  workEndTime: string;
  workDays: number[];
  updatedAt: string;
  dashboardEnabled?: boolean;
  screenshotQuality?: string;
  appTrackingEnabled?: boolean;
  urlTrackingEnabled?: boolean;
  presenceTrackingEnabled?: boolean;
  deviceTrackingEnabled?: boolean;
  wifiTrackingEnabled?: boolean;
  liveScreenEnabled?: boolean;
  liveScreenQuality?: string;
}

interface ViewDeviceSettingsProps {
  settings: DeviceSettingsData;
  onEdit: () => void;
}

const QUALITY_INFO: Record<string, string> = {
  low: "Low (960×540, 5 fps)",
  medium: "Medium (1280×720, 10 fps)",
  high: "High (1920×1080, 20 fps)",
};

const ViewDeviceSettings: React.FC<ViewDeviceSettingsProps> = ({
  settings,
  onEdit,
}) => {
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getDayName = (dayNum: number): string => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[dayNum] || "";
  };

  const StatusBadge = ({ enabled }: { enabled: boolean | undefined }) => (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${enabled
          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
        }`}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );

  // Safely format the date by ensuring it is treated as UTC
  let safeUpdatedAt = settings.updatedAt;
  if (
    safeUpdatedAt &&
    !safeUpdatedAt.endsWith("Z") &&
    !/[+-]\d{2}:\d{2}$/.test(safeUpdatedAt)
  ) {
    safeUpdatedAt = `${safeUpdatedAt}Z`;
  }

  const lastUpdated = convertUtcToIst(safeUpdatedAt);

  const screenshotQualityDisplay =
    QUALITY_INFO[(settings.screenshotQuality || "medium").toLowerCase()] ||
    QUALITY_INFO.medium;

  const liveScreenQualityDisplay =
    QUALITY_INFO[(settings.liveScreenQuality || "medium").toLowerCase()] ||
    QUALITY_INFO.medium;

  return (
    <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header Section */}
      <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Tracking Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {lastUpdated.date}, {lastUpdated.time}
          </p>
        </div>
        <HeaderButtons onClick={onEdit} variant="primary">
          <svg
            className="w-4 h-4 mr-2 inline"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          Edit Settings
        </HeaderButtons>
      </div>

      {/* Body Content */}
      <div className="p-6 space-y-8">
        {/* Screenshot Settings */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Screenshot Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Screenshot Capture
              </span>
              <StatusBadge enabled={settings.screenshotEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Screenshot Interval
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.screenshotInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Screenshot Quality
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {screenshotQualityDisplay}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* Live Screen Settings */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-pink-600 dark:text-pink-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Live Screen Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Live Screen Capture
              </span>
              <StatusBadge enabled={settings.liveScreenEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Live Screen Quality
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {liveScreenQualityDisplay}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* User Dashboard Settings */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-teal-600 dark:text-teal-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
            User Dashboard Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                User Dashboard
              </span>
              <StatusBadge enabled={settings.dashboardEnabled} />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* Feature Control Section */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-indigo-600 dark:text-indigo-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
            Feature Control
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                App Tracking
              </span>
              <StatusBadge enabled={settings.appTrackingEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                URL Tracking
              </span>
              <StatusBadge enabled={settings.urlTrackingEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Face Detection
              </span>
              <StatusBadge enabled={settings.presenceTrackingEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Device Tracking
              </span>
              <StatusBadge enabled={settings.deviceTrackingEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                WiFi Tracking
              </span>
              <StatusBadge enabled={settings.wifiTrackingEnabled} />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* Time Intervals */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Time Intervals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Device Check Interval
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.deviceCheckInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Session Upload
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.sessionUploadInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                URL Session Upload
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.urlSessionUploadInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Device Status Upload
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.deviceStatusUploadInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Presence Check
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.presenceCheckInterval)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Presence Upload
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.presenceUploadInterval)}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* Work Schedule */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Work Schedule
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Schedule Tracking
              </span>
              <StatusBadge enabled={settings.scheduleTrackingEnabled} />
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Work Hours
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {settings.workStartTime} - {settings.workEndTime}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 md:col-span-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Work Days
              </span>
              <div className="flex flex-wrap gap-1.5 justify-end">
                {settings.workDays
                  .sort((a, b) => a - b)
                  .map((day) => (
                    <span
                      key={day}
                      className="px-2 py-0.5 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-xs font-medium"
                    >
                      {getDayName(day)}
                    </span>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

        {/* Behaviour Settings */}
        <div>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-orange-600 dark:text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Behaviour Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-700/50 md:border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Idle Threshold
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.idleThreshold)}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 md:py-3 border-gray-100 dark:border-gray-700/50 border-b-0">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Daily Break Limit
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatDuration(settings.dailyBreakLimit)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDeviceSettings;